// import enums
import { EndOfEmploymentSettlementEnums } from './CLASettings.enums';

// import utils
import * as yup from 'yup';
import { convertToNullWhenNaN } from 'utils/validation';
import { getLocale } from 'utils/date';
import i18n from 'config/i18n';

const t = i18n.getFixedT(getLocale(), 'common', 'adminPortal.cla');

export const schema = (t) => {
  return yup.object({
    code: yup.string(),
    year: yup.number().required(t('year'))
  });
};

const commonNumberValidationSchema = () =>
  yup
    .number()
    .min(0, t('errors.minZero'))
    .nullable()
    .transform(convertToNullWhenNaN)
    .required(t('errors.required'));
export const commonBooleanValidationSchema = () => yup.boolean().default(false);

export const generalSchema = (t) =>
  yup.object({
    claId: yup.number().required(t('code')),
    claExternalCode: yup.string(),
    description: yup.string(),
    year: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required(t('errors.year')),
    workHoursBasedWeekdays: commonBooleanValidationSchema(),
    'flags.LEAVE_HOURS_EQUAL_PERIODS': commonBooleanValidationSchema(),
    'flags.PLB_VISIBILITY': commonBooleanValidationSchema(),
    hoursWorkingPerYear: commonNumberValidationSchema(),
    periodHours: commonNumberValidationSchema(),
    hoursWorkingPerWeek: commonNumberValidationSchema(),
    hoursWorkingPerDay: commonNumberValidationSchema(),
    hoursHolidayPerYear: commonNumberValidationSchema(),
    hoursPlbYear: commonNumberValidationSchema(),
    hoursHolidayYearMinimum: commonNumberValidationSchema(),
    plbName: yup
      .string()
      .max(15, t('errors.max15Chars'))
      .required(t('errors.required')),
    plbValidity: yup.string().default(''),
    contractHoursBasedOnWeekdays: yup.boolean()
  });

export const leaveSchema = yup.object({
  balanceBudgetName: yup.string(),
  balanceBudgetMaxGiftHours: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  'flags.LEAVE_WEEK_HOURS_MAX': yup.boolean(),
  'flags.LEAVE_HOURS_BASED_ON_WORK': yup.boolean(),
  'flags.LEAVE_HOURS_CONTRACT_FLOOR': yup.boolean(),
  'flags.LEAVE_HOURS_BASED_ON_PAYED': yup.boolean(),
  'flags.PLB_HOURS_BASED_ON_PAYED': yup.boolean(),
  'flags.LEAVE_HOURS_ZERO_HOUR_CONTRACT': yup.boolean(),
  'flags.PLB_HOURS_ZERO_HOUR_CONTRACT': yup.boolean(),
  'flags.HOURS_LEAVE_NUC_NEXT_YEAR': yup.boolean(),
  'flags.HOURS_PLB_NUC_NEXT_YEAR': yup.boolean(),
  'flags.FULL_MONTH_OF_LEAVE_WHEN_START_DURING_MONTH': yup.boolean(),
  startBeforeDayToReceiveFullMonthOfLeave: yup.number().min(1).max(31),
  endAfterDayToReceiveFullMonthOfLeave: yup.number().min(1).max(31),
  'flags.COMP_LEAVE_FIRST': yup.boolean(),
  'flags.COMP_LEAVE_EARMARKED': yup.boolean(),
  'flags.LEAVE_MIN_NEGATIVE': yup.boolean(),
  'flags.LEAVE_4_PLUS_BEFORE_MIN': yup.boolean(),
  'flags.AGE_HOURS_AS_LEAVE_OVER': yup.boolean(),
  'flags.BLOCK_FESTIVE_DAY_HOUR': yup.boolean(),
  'flags.COMP_LEAVE_EXACT_HOLIDAYS': yup.boolean(),
  hoursJusFestDays: yup.number().min(0).typeError(t('errors.invalidNumber')),
  'flags.JUS_URC': yup.boolean(),
  'flags.JUS_MIN_MAX': yup.boolean()
});

export const extraHourSchema = yup.object({
  extraThreshPeriod: yup.number().min(0).typeError(t('errors.invalidNumber')),
  extraMarginPeriod: yup.number().min(0).typeError(t('errors.invalidNumber')),
  extraThreshPeriodLast: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  extraMarginPeriodLast: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  'flags.EXTRA_HOURS_NOT_ROUNDED': yup.boolean(),
  extraMaxPeriodPaid: yup.number().min(0).typeError(t('errors.invalidNumber')),
  'flags.PROC_EXTRA_HOURS_NOMINAL': yup.boolean(),
  dccSvc: yup.number().default(10),
  procExtraNomDutyTypeId: yup.number().nullable(),
  capReferenceNetHours: yup.number().min(0).nullable()
});

export const capPlanSchema = yup.object({
  capReferenceNetHours: commonNumberValidationSchema()
});

export const deviatingReimbAttandanceSchema = yup.object({
  attendanceBaseHours: yup.number().min(0).typeError(t('errors.invalidNumber')),
  attendanceHolidayAllowanceHours: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  attendanceWeekendAllowanceHours: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  attendanceWeekdayAllowanceHours: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  attendanceHolidayAllowanceAmount: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  attendanceWeekendAllowanceAmount: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  attendanceWeekdayAllowanceAmount: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  nrOfDaysForAdditionalCompensation: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  onCallBaseHours: yup.number().min(0).typeError(t('errors.invalidNumber')),
  onCallHolidayAllowanceHours: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  onCallWeekendAllowanceHours: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  onCallWeekdayAllowanceHours: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  onCallHolidayAllowanceAmount: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  onCallWeekendAllowanceAmount: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber')),
  onCallWeekdayAllowanceAmount: yup
    .number()
    .min(0)
    .typeError(t('errors.invalidNumber'))
});

const nullableNumber = () =>
  yup
    .number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || isNaN(value) ? null : value
    );

export const endOfEmploymentSettlementSchema = yup.object({
  endOfEmploymentSettlementType: yup
    .string()
    .oneOf([
      EndOfEmploymentSettlementEnums.NONE,
      EndOfEmploymentSettlementEnums.PAY_OUT,
      EndOfEmploymentSettlementEnums.SETTLE,
      EndOfEmploymentSettlementEnums.TRANSFER
    ])
    .nullable()
    .transform((value) =>
      value === null ? EndOfEmploymentSettlementEnums.NONE : value
    )
    .default(EndOfEmploymentSettlementEnums.NONE),
  payOutExtraHoursDhtId: nullableNumber(),
  payOutLeaveHoursDhtId: nullableNumber(),
  payOutPlbHoursDhtId: nullableNumber(),
  payOutBalanceHoursDhtId: nullableNumber(),
  settleExtraHoursDhtId: nullableNumber(),
  settleLeaveHoursDhtId: nullableNumber(),
  settlePlbHoursDhtId: nullableNumber(),
  settleBalanceHoursDhtId: nullableNumber(),
  transferExtraHoursDhtId: nullableNumber(),
  transferLeaveHoursDhtId: nullableNumber(),
  transferPlbHoursDhtId: nullableNumber(),
  transferBalanceHoursDhtId: nullableNumber()
});

export const specialLeavesSchema = () =>
  yup.object({
    specialLeaveImportCode: yup
      .string()
      .trim()
      .required(t('validation.required')),

    specialLeaveDutyHourTypeId: yup
      .number()
      .typeError(t('validation.required'))
      .required(t('validation.required')),

    special_leaves: yup.array(
      yup.object({
        special_leave_id: yup.string().required(),
        special_leave_importCode: yup.string().required(),
        special_leave_dutyHourTypeId: yup.number().required()
      })
    )
  });
